import React, { forwardRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { MenuItem, MenuItems, MenuSeparator } from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Tooltip } from "src/design-system/Tooltip";
import { HeadlessUIPlacement } from "src/utils/headlessui";

type DividerElement = { key: "divider"; action?: undefined };
type HeaderElement = { key: "header"; action?: undefined; header: string };
type NavElement = {
  key: "nav";
  nav: string;
  action: () => void;
  icon?: IconProp;
  suffix?: React.ReactNode;
  subtitle?: React.ReactNode;
};

export type DropdownElement = {
  key: string;
  action: () => void;
  icon?: IconProp;
  disabled?: boolean | string;
  suffix?: React.ReactNode;
  subtitle?: React.ReactNode;
};

export type OptionsDropdownElement =
  | DropdownElement
  | DividerElement
  | HeaderElement
  | NavElement;

const isDivider = (
  element: OptionsDropdownElement,
): element is DividerElement => element === MENU_DIVIDER;

const isHeader = (element: OptionsDropdownElement): element is HeaderElement =>
  element.key === "header";

export const isNav = (element: OptionsDropdownElement): element is NavElement =>
  element.key === "nav";

export const MENU_DIVIDER: DividerElement = { key: "divider" };

const itemPaddings = "px-4 py-2.5";

type Props = {
  elements: OptionsDropdownElement[];
  getOnElementClick: (
    element: DropdownElement,
  ) => (event: React.MouseEvent<Element>) => void;
  timeout?: number;
  placement: HeadlessUIPlacement;
  static?: boolean;
};

export const OptionsDropdownItems = forwardRef<HTMLDivElement, Props>(
  (
    {
      elements,
      getOnElementClick: onElementClick,
      placement,
      static: isStatic = false,
      timeout = 500,
    },
    ref,
  ) => {
    return (
      <MenuItems
        ref={ref}
        anchor={{
          to: placement,
          gap: 5,
          padding: 5,
        }}
        as={MotionDropdownPanel}
        data-loc="options-menu"
        exitTimeoutMs={timeout}
        static={isStatic}
      >
        <div className="min-w-[224px] max-w-[300px]">
          {elements.map((element: OptionsDropdownElement, index) => {
            if (isDivider(element)) {
              return (
                <MenuSeparator
                  key={`divider-${index}`}
                  className="my-2.5 h-px border-none bg-gray-200"
                />
              );
            }

            if (isHeader(element)) {
              return (
                <MenuItem
                  key={`header-${element.header}`}
                  as="div"
                  className={twJoin(
                    "cursor-default font-inter-semibold-13px text-gray-800",
                    itemPaddings,
                  )}
                >
                  {element.header}
                </MenuItem>
              );
            }

            if (isNav(element)) {
              return (
                <MenuItem
                  key={`nav-${element.nav}`}
                  as="button"
                  className={twJoin(
                    "group flex max-w-full flex-col gap-y-2 enabled:hover:bg-gray-50",
                    itemPaddings,
                  )}
                  onClick={onElementClick(element)}
                >
                  <span className="flex w-full cursor-pointer items-center gap-x-2 font-inter-semibold-13px text-gray-800">
                    {element.icon && (
                      <Icon
                        color="text-gray-500"
                        icon={element.icon}
                        size="sm"
                      />
                    )}
                    <span className="truncate">{element.nav}</span>
                    {element.suffix && (
                      <span className="ml-auto">{element.suffix}</span>
                    )}
                  </span>
                  {element.subtitle && (
                    <span className="inline-block w-full text-left font-inter-normal-12px text-gray-600">
                      {element.subtitle}
                    </span>
                  )}
                </MenuItem>
              );
            }

            return (
              <MenuItem key={element.key}>
                {/* This div must exist to not have logger.warning about ref */}
                <div>
                  <Tooltip
                    body={element.disabled}
                    disabled={typeof element.disabled !== "string"}
                    placement="right"
                    asChild
                  >
                    <button
                      className={twJoin(
                        "group flex min-h-12 w-full cursor-pointer items-center gap-x-2 text-left font-inter-normal-13px text-gray-800 enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400",
                        itemPaddings,
                      )}
                      data-loc={element.key}
                      disabled={!!element.disabled}
                      onClick={onElementClick(element)}
                    >
                      {element.icon && (
                        <div className="self-start">
                          <Icon
                            color="text-gray-600"
                            icon={element.icon}
                            size="xs"
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate">{element.key}</p>
                        {element.subtitle && (
                          <p className="mt-0.5 font-inter-normal-12px text-gray-600">
                            {element.subtitle}
                          </p>
                        )}
                      </div>
                      {element.suffix && (
                        <span className="ml-auto">{element.suffix}</span>
                      )}
                    </button>
                  </Tooltip>
                </div>
              </MenuItem>
            );
          })}
        </div>
      </MenuItems>
    );
  },
);
